// src/pages/DashboardHome.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Box,
  TextField,
} from "@mui/material";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { firestore } from "../firebase";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import moment from "moment";
import { saveBookingCache, getBookingCache } from "../utils/bookingCache";

// Helper: converts an ISO string to a Date object.
const toDate = (val) => (val instanceof Date ? val : new Date(val));

// Helper: calculates the fractional number of days between two timestamps.
const getDaysBetween = (startTime, endTime) => (endTime - startTime) / 86400000;

// Helper: gets the timestamp for the last day of the current month.
const getLastDayOfMonth = () => {
  const currentDate = new Date();
  return new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getTime();
};

const DashboardHome = () => {
  const auth = getAuth();
  const [user, loadingAuth, authError] = useAuthState(auth);
  const navigate = useNavigate();

  // We'll derive shopId from the currentUser
  const userId = user ? user.uid : null;
  const [shopSlug, setShopSlug] = useState(null);

  const initialCached = getBookingCache() || [];

  const [bookings, setBookings] = useState(initialCached);
  const [totalBookings, setTotalBookings] = useState(initialCached.length);

  const [upcomingPickups, setUpcomingPickups] = useState(0);
  const [upcomingDropoffs, setUpcomingDropoffs] = useState(0);
  const [pickupTasks, setPickupTasks] = useState([]);
  const [dropoffTasks, setDropoffTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Navigation pages for shop owner.
  const navPages = [
    { name: "Revenue Dashboard", path: "/revenue" },
    { name: "Inventory", path: "/inventory" },
    { name: "Shop Profile", path: "/profile" },
  ];

  useEffect(() => {
    if (userId) {
      fetchShopSlug(userId).then((slug) => {
        if (slug) setShopSlug(slug);
      });
    }
  }, [userId]);

  useEffect(() => {
    if (!shopSlug) return;

    const loadFromCache = () => {
      const cached = getBookingCache();
      if (cached) {
        setBookings(cached);
        updateSummaries(cached);
      }
    };

    const fetchFromFirestore = async () => {
      try {
        const snapshot = await getDocs(
          collection(firestore, "rentalShops", shopSlug, "bookings")
        );
        const fetched = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((b) => b.type !== "blocked");
        setBookings(fetched);
        updateSummaries(fetched);
        saveBookingCache(fetched); // Save to cache
      } catch (e) {
        console.error("Error fetching bookings:", e);
      } finally {
        setLoading(false);
      }
    };

    const updateSummaries = (data) => {
      const now = Date.now();
      setTotalBookings(data.length);
      setUpcomingPickups(
        data.filter((b) => toDate(b.startDate).getTime() > now).length
      );
      setUpcomingDropoffs(
        data.filter((b) => toDate(b.endDate).getTime() > now).length
      );

      const dayStart = moment().startOf("day").valueOf();
      const dayEnd = moment().endOf("day").valueOf();

      setPickupTasks(
        data.filter((b) => {
          const start = toDate(b.startDate).getTime();
          return start >= dayStart && start <= dayEnd;
        })
      );

      setDropoffTasks(
        data.filter((b) => {
          const end = toDate(b.endDate).getTime();
          return end >= dayStart && end <= dayEnd;
        })
      );
    };

    loadFromCache(); // Load cached data first
    fetchFromFirestore(); // Then fetch fresh data
  }, [shopSlug]);

  const fetchShopSlug = async (userId) => {
    const ownerDocRef = doc(firestore, "users", userId);
    const ownerSnap = await getDoc(ownerDocRef);
    if (ownerSnap.exists()) {
      return ownerSnap.data().shopId;
    } else {
      console.error("No shop assigned to this user.");
      return null;
    }
  };

  if (loadingAuth && bookings.length === 0) {
    return (
      <Container maxWidth="md">
        <Typography variant="h6" align="center">
          Loading dashboard data...
        </Typography>
      </Container>
    );
  }

  if (authError) {
    return (
      <Container maxWidth="md">
        <Typography variant="h6" align="center" color="error">
          Error loading authentication.
        </Typography>
      </Container>
    );
  }

  return (
    <Container style={{ marginTop: "2rem" }}>
      <Typography variant="h4" align="left" gutterBottom>
        Dashboard
      </Typography>

      {/* Summary Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1">Total Bookings</Typography>
              <Typography variant="h6">{totalBookings}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1">Upcoming Pickups</Typography>
              <Typography variant="h6">{upcomingPickups}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1">Upcoming Drop-offs</Typography>
              <Typography variant="h6">{upcomingDropoffs}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Navigation Section */}
      <Box mb={4}>
        <Grid container spacing={2}>
          {navPages.map((page) => (
            <Grid item xs={12} sm={4} key={page.name}>
              <Card>
                <CardActionArea component={Link} to={page.path}>
                  <CardContent>
                    <Typography variant="h5" component="div">
                      {page.name}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Today's Tasks Section */}
      <Box mt={4}>
        <Typography variant="h5" gutterBottom>
          Today's Tasks
        </Typography>
        <Grid container spacing={4}>
          {/* Pick-ups */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Today's Pick-ups
            </Typography>
            {pickupTasks.length === 0 ? (
              <Typography variant="body1">
                No pick-ups scheduled for today.
              </Typography>
            ) : (
              <List>
                {pickupTasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <ListItem>
                      <ListItemText
                        primary={`${task.label}${
                          task.plateNr ? ` - ${task.plateNr}` : ""
                        }`}
                        secondary={`Pick-up Time: ${moment(
                          task.startDate
                        ).format("LT")}`}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Grid>
          {/* Drop-offs */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Today's Drop-offs
            </Typography>
            {dropoffTasks.length === 0 ? (
              <Typography variant="body1">
                No drop-offs scheduled for today.
              </Typography>
            ) : (
              <List>
                {dropoffTasks.map((task) => (
                  <React.Fragment key={task.id}>
                    <ListItem>
                      <ListItemText
                        primary={`${task.label}${
                          task.plateNr ? ` - ${task.plateNr}` : ""
                        }`}
                        secondary={`Drop-off Time: ${moment(
                          task.endDate
                        ).format("LT")}`}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Grid>
        </Grid>
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/timeline"
          >
            View Timeline
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default DashboardHome;
